/* @jsxRuntime classic */
/** @jsx createElement */
import {
  BodyThree,
  BodyTwo,
  createElement,
  HeadlineFour,
  HeadlineThree,
  HeadlineTwo,
  OverlineFive,
  OverlineOne,
  OverlineThree,
} from '@nucleus/ncu-section-builder';
import { Height, InfoMaxWidth, SectionLayout, SectionTypeEnum } from '@nucleus/types/web';
import { Alignment } from '@nucleus/types/web/alignment';
import { Position } from '@nucleus/types/web';
import { SectionDefaultBuilder } from '../SectionDefaultBuilder';
import { ExtractPayload } from '@nucleus/types/web/section-data';
import {
  StandardBlockButtons,
  StandardSectionBackground,
  blockMediaOptionsDefault,
  buildPlaceholderMedia,
  buildPresetMedia,
  cardMediaOptionsBackgroundDefault,
  cardMediaOptionsInlineDefault,
} from '../helpers';
import { defaultText } from '../text';

const builder = new SectionDefaultBuilder<SectionTypeEnum.Card>()
  .setWrapper((config, layoutPayload) => ({
    name: config.title,
    blockLayout: config.layout,
    layout: SectionLayout.Full,
    cardType: 'grid',
    blockMediaOptions: blockMediaOptionsDefault(config.lookConfig),
    ...layoutPayload,
  } as ExtractPayload<SectionTypeEnum.Card>))
  .addLayout('layout1', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-1',
    height: Height.Medium,
    blockLayoutVariant: 'variant2',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('cardBackground', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: false, headline: true, body: true, buttons: true },
        itemInfoPosition: 'top-left',
        itemMediaDisplayType: 'inline',
        itemMediaOptions: cardMediaOptionsInlineDefault(lookConfig),
        itemSpacing: 'constrained',
        gridAlignment: 'center',
        gridTypeDesktop: 'swipe',
        gridTypeMobile: 'swipe',
      },
    ],
  }))
  .addLayout('layout2', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Large,
    blockLayoutVariant: 'variant2',
    blockEnabledInfo: { overline: false, headline: false, byline: false, body: false },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Center,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        headline: <HeadlineTwo>{defaultText.headline.short}</HeadlineTwo>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('cardBackground', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: true, headline: true, body: true, buttons: true },
        itemInfoPosition: 'top-left',
        itemMediaDisplayType: 'background',
        itemMediaOptions: cardMediaOptionsBackgroundDefault(lookConfig),
        itemSpacing: 'constrained',
        gridAlignment: 'center',
        gridTypeDesktop: 'standard',
        gridTypeMobile: 'standard',
      },
    ],
  }))
  .addLayout('layout3', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-3',
    height: Height.Medium,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: false, headline: false, byline: false, body: false },
    blockInfoPosition: Position.Left,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Small,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.standard}</BodyTwo>,
        actions: [],
        mediaItems: [
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
          buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh }),
        ],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('cardBackground', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: false, headline: true, body: true, buttons: true },
        itemInfoPosition: 'top-left',
        itemMediaDisplayType: 'inline',
        itemMediaOptions: cardMediaOptionsInlineDefault(lookConfig),
        itemSpacing: 'constrained',
        gridAlignment: 'center',
        gridTypeDesktop: 'standard',
        gridTypeMobile: 'standard',
      },
    ],
  }))
  .addLayout('layout4', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Medium,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Center,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockBodyMaxColumns: 2,
    blockButtonsAlignment: Alignment.Center,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineThree>{defaultText.overline.standard}</OverlineThree>,
        headline: <HeadlineThree>{defaultText.headline.short}</HeadlineThree>,
        body: [
          ...BodyTwo({ children: defaultText.body.long }),
          ...BodyTwo({ children: '' }),
          ...BodyTwo({ children: defaultText.body.long2 }),
          ...BodyTwo({ children: '' }),
          ...BodyTwo({ children: defaultText.body.long3 }),
        ],
        actions: [],
        mediaItems: [],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('cardBackground', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: true, headline: true, body: true, buttons: true },
        itemInfoPosition: 'top-left',
        itemMediaDisplayType: 'inline',
        itemMediaOptions: cardMediaOptionsInlineDefault(lookConfig),
        itemSpacing: 'constrained',
        gridAlignment: 'center',
        gridTypeDesktop: 'swipe',
        gridTypeMobile: 'swipe',
      },
    ],
  }))
  .addLayout('layout5', ({ lookConfig }) => ({
    backgroundMedia: { presetMediaKey: 'sectionBackground', presetMediaOrder: 'first' },
    blockBackgroundMedia: {
      useImage: true,
      image: lookConfig?.placeholders?.LightBokeh,
      presetMediaKey: 'blockBackground',
      presetMediaOrder: 'first',
    },
    colorPaletteVariation: 'variation-2',
    height: Height.Large,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Center,
    blockHeadlineAlignment: Alignment.Right,
    blockHeadlineMaxWidth: InfoMaxWidth.Medium,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineOne>{defaultText.overline.standard}</OverlineOne>,
        headline: <HeadlineThree>{defaultText.headline.long}</HeadlineThree>,
        body: <BodyTwo>{defaultText.body.short}</BodyTwo>,
        actions: [],
        mediaItems: [],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('cardBackground', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: true, headline: true, body: true, buttons: true },
        itemInfoPosition: 'top-left',
        itemMediaDisplayType: 'background',
        itemMediaOptions: cardMediaOptionsBackgroundDefault(lookConfig),
        itemSpacing: 'full',
        gridAlignment: 'center',
        gridTypeDesktop: 'standard',
        gridTypeMobile: 'standard',
      },
    ],
  }))
  .addLayout('layout6', ({ lookConfig }) => ({
    ...StandardSectionBackground,
    colorPaletteVariation: 'variation-2',
    height: Height.Medium,
    blockLayoutVariant: 'variant1',
    blockEnabledInfo: { overline: true, headline: true, byline: false, body: true },
    blockInfoPosition: Position.Left,
    blockHeadlineAlignment: Alignment.Left,
    blockHeadlineMaxWidth: InfoMaxWidth.Large,
    blockBodyAlignment: Alignment.Left,
    blockButtonsAlignment: Alignment.Left,
    ...StandardBlockButtons,
    blocks: [
      {
        overline: <OverlineFive>{defaultText.overline.standard}</OverlineFive>,
        headline: <HeadlineFour>{defaultText.headline.short}</HeadlineFour>,
        body: <BodyThree>{defaultText.body.standard}</BodyThree>,
        actions: [],
        mediaItems: [buildPlaceholderMedia({ useImage: true, image: lookConfig?.placeholders?.LightBokeh })],
        itemAlignment: 'left',
        itemBackgroundMedia: buildPresetMedia('cardBackground', lookConfig),
        itemEnabledInfo: { mediaItems: true, overline: true, headline: true, body: true, buttons: true },
        itemInfoPosition: 'top-left',
        itemMediaDisplayType: 'inline',
        itemMediaOptions: cardMediaOptionsInlineDefault(lookConfig),
        itemSpacing: 'constrained',
        gridAlignment: 'center',
        gridTypeDesktop: 'standard',
        gridTypeMobile: 'swipe',
      },
    ],
  }));

export default builder.build();
